import { useState, useCallback, useEffect, useRef } from 'react';
import { generateQuestion } from '../../../utils/mathProblems';
import { initMatchState, applyPoint } from '../../../utils/tennisScore';
import type { MatchState } from '../../../utils/tennisScore';
import type { Question } from '../../../utils/mathProblems';
import TennisFirstPerson from '../../../components/game/TennisFirstPerson';
import TennisMiniGame from '../../../components/game/TennisMiniGame';
import type { BallState } from '../../../components/game/TennisFirstPerson';
import TennisBoardScore from '../../../components/game/TennisBoardScore';
import QuestionCard from '../../../components/game/QuestionCard';
import AnswerOptions from '../../../components/game/AnswerOptions';
import type { RoomInfo, GameBroadcast } from '../../../hooks/useOnlineRoom';

type GamePhase = 'loading' | 'question' | 'rally' | 'result' | 'skipped' | 'over';

interface Props {
  room: RoomInfo;
  isHost: boolean;
  playerId: string;
  send: (e: GameBroadcast) => void;
  onEvent: (fn: (e: GameBroadcast) => void) => void;
  onLeave: () => void;
  finishGame: () => void;
}

export default function OnlineGameBoard({ room, isHost, playerId, send, onEvent, onLeave, finishGame }: Props) {
  const [phase, setPhase] = useState<GamePhase>('loading');
  const [question, setQuestion] = useState<Question | null>(null);
  const [round, setRound] = useState(0);
  const [myAnswerState, setMyAnswerState] = useState<'idle' | 'correct' | 'wrong' | 'waiting'>('idle');
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [matchState, setMatchState] = useState<MatchState>(initMatchState());
  const [remoteBall, setRemoteBall] = useState<BallState | null>(null);
  const [remoteOpponentX, setRemoteOpponentX] = useState(0);
  const [rallyWinner, setRallyWinner] = useState<'me' | 'opponent' | null>(null);
  const [opponentAnswered, setOpponentAnswered] = useState(false);
  const [perspective, setPerspective] = useState<'first' | 'third'>('first');

  const roundRef = useRef(0);
  const matchRef = useRef(matchState);
  const roundLocked = useRef(false);
  const hostAnswered = useRef(false);
  const guestAnswered = useRef(false);
  const hostCorrect = useRef(false);
  const guestCorrect = useRef(false);
  const qTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const matchStarted = useRef(false);

  useEffect(() => { matchRef.current = matchState; }, [matchState]);

  const myName = isHost ? room.host_name : (room.guest_name ?? 'You');
  const opponentName = isHost ? (room.guest_name ?? 'Opponent') : room.host_name;

  // ── Start next round (host only) ─────────────────────────────────────
  const startNextRound = useCallback(() => {
    if (!isHost) return;
    hostAnswered.current = false;
    guestAnswered.current = false;
    hostCorrect.current = false;
    guestCorrect.current = false;
    roundLocked.current = false;
    if (qTimerRef.current) clearTimeout(qTimerRef.current);

    const q = generateQuestion(room.game_type, room.difficulty);
    const newRound = roundRef.current + 1;
    roundRef.current = newRound;
    setRound(newRound);
    setQuestion(q);
    setMyAnswerState('idle');
    setSelectedAnswer(null);
    setRallyWinner(null);
    setOpponentAnswered(false);
    setPhase('question');

    send({ event: 'round_start', question: q, round: newRound });

    // 25 second timeout — time's up, give point to guest
    qTimerRef.current = setTimeout(() => {
      if (!roundLocked.current) {
        roundLocked.current = true;
        const newMatch = applyPoint(matchRef.current, 'opponent');
        setMatchState(newMatch);
        setRallyWinner('opponent');
        send({ event: 'rally_end', winner: 'guest' });
        setPhase('result');
        if (!newMatch.matchOver) setTimeout(() => startNextRound(), 2500);
        else { setPhase('over'); finishGame(); }
      }
    }, 25000);
  }, [isHost, room, send, finishGame]);

  // ── Resolve answers (host only) ──────────────────────────────────────
  const checkAnswers = useCallback(() => {
    if (roundLocked.current) return;

    if (hostCorrect.current) {
      roundLocked.current = true;
      if (qTimerRef.current) clearTimeout(qTimerRef.current);
      setPhase('rally');
      send({ event: 'rally_start', serverId: playerId });
      return;
    }
    if (guestCorrect.current) {
      roundLocked.current = true;
      if (qTimerRef.current) clearTimeout(qTimerRef.current);
      setPhase('rally');
      send({ event: 'rally_start', serverId: room.guest_id ?? '' });
      return;
    }
    if (hostAnswered.current && guestAnswered.current) {
      // Both answered wrong — no point awarded, skip to next round
      roundLocked.current = true;
      if (qTimerRef.current) clearTimeout(qTimerRef.current);
      send({ event: 'round_skip' });
      setPhase('skipped');
      setTimeout(() => startNextRound(), 2500);
    }
  }, [playerId, room.guest_id, send, startNextRound]);

  // ── Submit answer ────────────────────────────────────────────────────
  const submitAnswer = useCallback((answer: string) => {
    if (myAnswerState !== 'idle' || !question) return;
    const correct = answer === question.correctAnswer;
    setSelectedAnswer(answer);
    setMyAnswerState(correct ? 'waiting' : 'wrong');
    send({ event: 'answer', playerId, answer, correct });

    if (isHost) {
      hostAnswered.current = true;
      hostCorrect.current = correct;
      // Small delay to let guest's near-simultaneous answer arrive first
      setTimeout(() => checkAnswers(), 280);
    }
  }, [myAnswerState, question, playerId, isHost, send, checkAnswers]);

  // ── Handle rally end (host) ──────────────────────────────────────────
  const handleHostRallyEnd = useCallback((winner: 'player' | 'opponent') => {
    const broadcastWinner = winner === 'player' ? 'host' : 'guest';
    send({ event: 'rally_end', winner: broadcastWinner });
    const newMatch = applyPoint(matchRef.current, winner);
    setMatchState(newMatch);
    setRallyWinner(winner === 'player' ? 'me' : 'opponent');
    setPhase('result');
    if (!newMatch.matchOver) {
      setTimeout(() => startNextRound(), 2500);
    } else {
      setTimeout(() => setPhase('over'), 2500);
      finishGame();
    }
  }, [send, startNextRound, finishGame]);

  // ── Incoming event handler ────────────────────────────────────────────
  useEffect(() => {
    onEvent((e) => {
      if (e.event === 'round_start') {
        if (!isHost) {
          const q = e.question as Question;
          roundRef.current = e.round;
          setRound(e.round);
          setQuestion(q);
          setMyAnswerState('idle');
          setSelectedAnswer(null);
          setRallyWinner(null);
          setOpponentAnswered(false);
          setPhase('question');
        }
      }

      if (e.event === 'answer') {
        if (isHost && e.playerId !== playerId) {
          guestAnswered.current = true;
          guestCorrect.current = e.correct;
          setOpponentAnswered(true);
          checkAnswers();
        }
        if (!isHost && e.playerId !== playerId) {
          // Opponent (host) answered — show indicator
          setOpponentAnswered(true);
        }
      }

      if (e.event === 'rally_start') {
        if (!isHost) setPhase('rally');
      }

      if (e.event === 'ball') {
        if (!isHost) setRemoteBall(e.ball);
      }

      if (e.event === 'paddle') {
        if (isHost) setRemoteOpponentX(e.x);
      }

      if (e.event === 'rally_end') {
        if (!isHost) {
          const meWon = e.winner === 'guest';
          const localWinner: 'player' | 'opponent' = meWon ? 'player' : 'opponent';
          const newMatch = applyPoint(matchRef.current, localWinner);
          setMatchState(newMatch);
          setRallyWinner(meWon ? 'me' : 'opponent');
          setPhase(newMatch.matchOver ? 'over' : 'result');
          if (newMatch.matchOver) finishGame();
        }
      }

      // Both answered wrong — no point given, skip round
      if (e.event === 'round_skip') {
        if (!isHost) {
          setPhase('skipped');
          // No applyPoint here — host handles the timing and starts next round
        }
      }

      if (e.event === 'opponent_ready') {
        setOpponentAnswered(false);
      }
    });
  }, [isHost, playerId, onEvent, checkAnswers, finishGame]);

  // ── Init: host starts first round after opponent joins ────────────────
  useEffect(() => {
    if (isHost && room.status === 'playing' && phase === 'loading' && !matchStarted.current) {
      matchStarted.current = true;
      send({ event: 'opponent_ready' });
      setTimeout(() => startNextRound(), 1800);
    }
  }, [isHost, room.status, phase, startNextRound, send]);

  // Guest: announce ready
  useEffect(() => {
    if (!isHost && phase === 'loading') {
      send({ event: 'opponent_ready' });
    }
  }, [isHost, phase, send]);

  // ── Match Over ────────────────────────────────────────────────────────
  if (phase === 'over') {
    const iWon = matchState.winner === 'player';
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center">
        <div className={`w-24 h-24 flex items-center justify-center rounded-full ${iWon ? 'bg-lime-100' : 'bg-red-100'}`}>
          <i className={`text-5xl ${iWon ? 'ri-trophy-fill text-yellow-500' : 'ri-emotion-sad-line text-red-400'}`} />
        </div>
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em', color: iWon ? '#166534' : '#b91c1c' }}>
            {iWon ? 'YOU WIN!' : 'YOU LOSE!'}
          </h2>
          <p className="text-gray-500 mt-1">{iWon ? `Congrats ${myName}! You beat ${opponentName}!` : `${opponentName} wins this match!`}</p>
        </div>
        <div className="bg-sky-900 rounded-xl px-8 py-4 text-white">
          <p className="text-sky-300 text-xs uppercase tracking-widest mb-2">Final Sets</p>
          <div className="flex gap-6 text-2xl font-bold">
            <span className="text-lime-400">{myName} {matchState.sets.filter(s => s.player > s.opponent).length}</span>
            <span className="text-gray-400">–</span>
            <span className="text-orange-400">{matchState.sets.filter(s => s.opponent > s.player).length} {opponentName}</span>
          </div>
        </div>
        <button onClick={onLeave} className="px-7 py-2 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-500 cursor-pointer whitespace-nowrap">
          Back to Lobby
        </button>
      </div>
    );
  }

  // ── Rally Phase ────────────────────────────────────────────────────────
  if (phase === 'rally') {
    return (
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
          <div className="flex flex-col items-center">
            {perspective === 'first' ? (
              <TennisFirstPerson
                key={`rally-${round}`}
                onRallyEnd={isHost ? handleHostRallyEnd : () => {}}
                difficulty={room.difficulty}
                isGuest={!isHost}
                externalBall={!isHost ? remoteBall : undefined}
                remoteOpponentX={remoteOpponentX}
                onBallUpdate={isHost ? (b) => send({ event: 'ball', ball: b }) : undefined}
                onPaddleUpdate={!isHost ? (x) => send({ event: 'paddle', x }) : undefined}
                opponentLabel={isHost ? opponentName : room.host_name}
              />
            ) : (
              <TennisMiniGame
                key={`rally-3rd-${round}`}
                onRallyEnd={isHost ? handleHostRallyEnd : () => {}}
                difficulty={room.difficulty}
                isGuest={!isHost}
                externalBall={!isHost ? remoteBall : undefined}
                remoteOpponentX={remoteOpponentX}
                onBallUpdate={isHost ? (b) => send({ event: 'ball', ball: b }) : undefined}
                onPaddleUpdate={!isHost ? (x) => send({ event: 'paddle', x }) : undefined}
                opponentLabel={isHost ? opponentName : room.host_name}
              />
            )}
          </div>
          <div className="flex flex-col gap-4">
            <TennisBoardScore matchState={matchState} playerLabel={myName} opponentLabel={opponentName} />
            <div className="bg-sky-50 rounded-xl p-4 text-sm text-sky-800">
              <p className="font-semibold mb-1">
                <i className={`${perspective === 'first' ? 'ri-eye-line' : 'ri-vidicon-line'} mr-1`} />
                {perspective === 'first' ? 'First-Person' : '3rd-Person'} Controls
              </p>
              <ul className="space-y-1 text-xs text-sky-700">
                <li>• <kbd className="bg-white border border-sky-200 rounded px-1">A</kbd> / <kbd className="bg-white border border-sky-200 rounded px-1">D</kbd> or <kbd className="bg-white border border-sky-200 rounded px-1">←</kbd> <kbd className="bg-white border border-sky-200 rounded px-1">→</kbd> — Move</li>
                {perspective === 'first' && !isHost && <li className="text-orange-600">• Controls are mirrored — you are at the far end!</li>}
                {perspective === 'third' && !isHost && <li className="text-orange-600">• You are the player at the top of the court</li>}
              </ul>
            </div>
            <button onClick={onLeave} className="py-2 rounded-xl border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1" />Forfeit Match
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Result / Skipped Banner ─────────────────────────────────────────────
  const resultBanner = phase === 'result' && rallyWinner && (
    <div className={`rounded-xl px-5 py-4 flex items-center gap-3 mb-2 ${rallyWinner === 'me' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
      <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${rallyWinner === 'me' ? 'bg-green-100' : 'bg-red-100'}`}>
        <i className={`text-xl ${rallyWinner === 'me' ? 'ri-trophy-line text-green-600' : 'ri-close-circle-line text-red-500'}`} />
      </div>
      <div>
        <p className={`font-semibold text-sm ${rallyWinner === 'me' ? 'text-green-700' : 'text-red-700'}`}>
          {rallyWinner === 'me' ? 'You won the rally!' : `${opponentName} wins the rally!`}
        </p>
        <p className={`text-xs mt-0.5 ${rallyWinner === 'me' ? 'text-green-600' : 'text-red-500'}`}>
          {isHost ? 'Starting next question…' : 'Waiting for host…'}
        </p>
      </div>
    </div>
  );

  const skippedBanner = phase === 'skipped' && (
    <div className="rounded-xl px-5 py-4 flex items-center gap-3 mb-2 bg-gray-50 border border-gray-200">
      <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 bg-gray-100">
        <i className="text-xl ri-refresh-line text-gray-500" />
      </div>
      <div>
        <p className="font-semibold text-sm text-gray-700">Both players answered wrong — no point!</p>
        <p className="text-xs mt-0.5 text-gray-500">{isHost ? 'Starting next question…' : 'Waiting for next question…'}</p>
      </div>
    </div>
  );

  // ── Loading Screen ─────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <span className="relative flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-4 w-4 bg-sky-500" />
        </span>
        <p className="text-gray-500 text-sm">{isHost ? 'Starting match…' : 'Waiting for host to start…'}</p>
      </div>
    );
  }

  // ── Question / Result / Skipped Phase ────────────────────────────────────────────
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
      <div className="flex flex-col gap-4">
        {resultBanner}
        {skippedBanner}
        {question && (phase === 'question' || phase === 'result' || phase === 'skipped') && (
          <>
            <QuestionCard
              question={question}
              roundNumber={round}
              answerState={myAnswerState === 'wrong' ? 'wrong' : myAnswerState === 'correct' || myAnswerState === 'waiting' ? 'correct' : 'idle'}
              timeLeft={null}
              difficulty={room.difficulty}
            />
            <AnswerOptions
              question={question}
              onAnswer={submitAnswer}
              answerState={myAnswerState === 'wrong' ? 'wrong' : myAnswerState === 'correct' || myAnswerState === 'waiting' ? 'correct' : 'idle'}
              selectedAnswer={selectedAnswer}
              disabled={myAnswerState !== 'idle' || phase !== 'question'}
            />

            {/* Opponent answered indicator */}
            {phase === 'question' && opponentAnswered && myAnswerState === 'idle' && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-2 text-sm text-amber-700">
                <i className="ri-check-line text-amber-500" />
                {opponentName} has answered — hurry up!
              </div>
            )}

            {myAnswerState === 'waiting' && (
              <div className="rounded-xl bg-sky-50 border border-sky-200 px-4 py-3 flex items-center gap-2 text-sm text-sky-700">
                <span className="animate-spin inline-block w-4 h-4 border-2 border-sky-400 border-t-transparent rounded-full" />
                Correct! Waiting for the rally to start…
              </div>
            )}
            {myAnswerState === 'wrong' && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <i className="ri-close-circle-line mr-1" />
                Wrong! The correct answer was <strong>{question.correctAnswer}</strong>.
                {opponentAnswered
                  ? ' Both answered — no point scored this round.'
                  : ' Waiting to see if your opponent answers correctly…'}
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <TennisBoardScore matchState={matchState} playerLabel={myName} opponentLabel={opponentName} />

        {/* Camera view toggle */}
        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
          <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-widest">Camera View</p>
          <div className="flex gap-1.5 bg-gray-200 rounded-full p-1">
            <button
              onClick={() => setPerspective('third')}
              className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                perspective === 'third'
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-vidicon-line mr-1" />
              3rd Person
            </button>
            <button
              onClick={() => setPerspective('first')}
              className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                perspective === 'first'
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className="ri-eye-line mr-1" />
              1st Person
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1.5 text-center">
            {perspective === 'third' ? 'Top-down view of both players' : 'Immersive first-person view'}
          </p>
        </div>

        <div className="bg-sky-50 rounded-xl p-4 text-sm text-sky-800">
          <p className="font-semibold mb-1"><i className="ri-global-line mr-1" />Online Match</p>
          <ul className="space-y-1 text-xs text-sky-700">
            <li>• Answer correctly → tennis rally</li>
            <li>• Win the rally → you earn the point</li>
            <li>• Both wrong → no point, next question</li>
            <li>• {perspective === 'third' ? '3rd-person' : 'First-person'} view in every rally!</li>
          </ul>
        </div>
        <button onClick={onLeave} className="py-2 rounded-xl border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line mr-1" />Forfeit & Leave
        </button>
      </div>
    </div>
  );
}
